import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Users, DollarSign, Clock, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

export default function VendorLedger({ branchId, token, t, lang, onDataReady }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/api/accounting/vendors?branch_id=${branchId || ''}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(d);
        if (onDataReady) onDataReady(d);
      } catch { toast.error(t('common.failed')); }
      setLoading(false);
    };
    fetch();
  }, [branchId, token, t]);

  const toggleVendor = async (name) => {
    if (expanded === name) { setExpanded(null); return; }
    setExpanded(name);
    try {
      const { data: d } = await axios.get(`${API}/api/accounting/vendors/${encodeURIComponent(name)}/transactions?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTransactions(d.transactions || []);
    } catch { setTransactions([]); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const statusBadge = (status) => {
    const styles = { paid: 'bg-emerald-500/10 text-emerald-600', pending: 'bg-amber-500/10 text-amber-600', due: 'bg-red-500/10 text-red-600' };
    return <Badge className={`text-[10px] py-0 px-1.5 ${styles[status] || ''}`}>{t(`accounting.${status}`)}</Badge>;
  };

  return (
    <div className="space-y-5" data-testid="vendor-ledger-tab">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('vendor.totalVendors')}</p>
          <p className="text-xl font-bold text-foreground">{data.summary.total_vendors}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('vendor.totalPaid')}</p>
          <p className="text-xl font-bold text-emerald-500">{fmt(data.summary.total_paid)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('vendor.totalPending')}</p>
          <p className={`text-xl font-bold ${data.summary.total_pending > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{fmt(data.summary.total_pending)}</p>
        </CardContent></Card>
      </div>

      {/* Vendor List */}
      {data.vendors.length === 0 ? (
        <div className="text-center py-12" data-testid="vendor-empty">
          <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t('vendor.noVendors')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('vendor.noVendorsDesc')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.vendors.map(v => (
            <Card key={v.name} className={expanded === v.name ? 'ring-1 ring-amber-500/30' : ''} data-testid={`vendor-row-${v.name}`}>
              <CardContent className="p-0">
                <button className="w-full p-4 flex items-center gap-3 text-start" onClick={() => toggleVendor(v.name)}>
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{v.name}</span>
                      {v.total_pending > 0 && <Badge className="bg-red-500/10 text-red-500 text-[10px] py-0">{t('vendor.hasPending')}</Badge>}
                    </div>
                    <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                      <span>{v.transaction_count} {t('vendor.transactions')}</span>
                      <span>{t('vendor.lastPayment')}: {v.last_payment_date || '-'}</span>
                    </div>
                  </div>
                  <div className="text-end flex-shrink-0">
                    <p className="text-sm font-bold">{fmt(v.total_amount)} <span className="text-xs font-normal text-muted-foreground">{t('common.sar')}</span></p>
                    <div className="flex gap-2 text-[10px] justify-end mt-0.5">
                      <span className="text-emerald-500">{t('accounting.paid')}: {fmt(v.total_paid)}</span>
                      {v.total_pending > 0 && <span className="text-red-500">{t('accounting.pending')}: {fmt(v.total_pending)}</span>}
                    </div>
                  </div>
                  {expanded === v.name ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>

                {/* Expanded transactions */}
                {expanded === v.name && (
                  <div className="border-t bg-muted/20 p-4 space-y-2">
                    {transactions.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center">{t('common.loading')}</p>
                    ) : transactions.map((tx, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-dashed last:border-0">
                        <div>
                          <span className="text-foreground">{t(`accounting.${toCamelCase(tx.category)}`)}</span>
                          {tx.description && <span className="text-xs text-muted-foreground ms-2">{tx.description}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          {statusBadge(tx.status)}
                          <span className="font-medium">{fmt(tx.amount)}</span>
                          <span className="text-xs text-muted-foreground">{tx.date}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
